type SizeMap = Readonly<{ small: string; medium: string; large: string }>;

type Rectangle = Readonly<{ key: string; sizes: SizeMap }>;

type Paragraph = Readonly<{ key: string; gaps: SizeMap; rectangles: readonly Rectangle[] }>;

const rect = (key: string, sizes: SizeMap): Rectangle => ({ key, sizes });
const paragraph = (key: string, rectangles: readonly Rectangle[], gaps: SizeMap): Paragraph => ({
	key,
	gaps,
	rectangles,
});

const GAPS = {
	petitionTitle: { small: 'gap-1', medium: 'gap-2', large: 'gap-2.5' } as const,
	messageParagraph: { small: 'gap-0.5', medium: 'gap-[5px]', large: 'gap-1.5' } as const,
	message: { small: 'gap-2', medium: 'gap-4', large: 'gap-5' } as const,
	messageFooter: { small: 'gap-1.5', medium: 'gap-3', large: 'gap-3' } as const,
} as const;

const petitionTitleRectangles: readonly Rectangle[] = [
	rect('petitionTitleRect1', { small: 'h-1 w-[42px]', medium: 'h-[9px] w-[97px]', large: 'h-[11px] w-[114px]' }),
	rect('petitionTitleRect2', { small: 'h-1 w-[49px]', medium: 'h-[9px] w-[110px]', large: 'h-[11px] w-[135px]' }),
	rect('petitionTitleRect3', { small: 'h-1 w-[36px]', medium: 'w-[81px] h-[9px]', large: 'h-[11px] w-[94px]' }),
];

// Message paragraphs data (each entry: paragraph key + rectangle specs)
const messageParagraphDefs: ReadonlyArray<[key: string, sizes: ReadonlyArray<[key: string, sizeMap: SizeMap]>]> = [
	[
		'messageParagraph1',
		[
			[
				'messageParagraph1Rect1',
				{ small: 'h-[3px] w-[90px]', medium: 'h-[7px] w-[177px]', large: 'h-[8px] w-[211px]' },
			],
			[
				'messageParagraph1Rect2',
				{ small: 'h-[3px] w-[80px]', medium: 'h-[7px] w-[140px]', large: 'h-[8px] w-[164px]' },
			],
		],
	],
	[
		'messageParagraph2',
		[
			[
				'messageParagraph2Rect1',
				{ small: 'h-[3px] w-[86px]', medium: 'h-[7px] w-[156px]', large: 'h-[8px] w-[182px]' },
			],
			[
				'messageParagraph2Rect2',
				{ small: 'h-[3px] w-[90px]', medium: 'h-[7px] w-[165px]', large: 'h-[8px] w-[193px]' },
			],
			[
				'messageParagraph2Rect3',
				{ small: 'h-[3px] w-[95px]', medium: 'h-[7px] w-[177px]', large: 'h-[8px] w-[211px]' },
			],
			[
				'messageParagraph2Rect4',
				{ small: 'h-[3px] w-[82px]', medium: 'h-[7px] w-[146px]', large: 'h-[8px] w-[170px]' },
			],
			[
				'messageParagraph2Rect5',
				{ small: 'h-[3px] w-[90px]', medium: 'h-[7px] w-[172px]', large: 'h-[8px] w-[201px]' },
			],
			[
				'messageParagraph2Rect6',
				{ small: 'h-[3px] w-[50px]', medium: 'h-[7px] w-[100px]', large: 'h-[8px] w-[116px]' },
			],
		],
	],
];

const messageParagraphs: readonly Paragraph[] = messageParagraphDefs.map(([pKey, rectDefs]) =>
	paragraph(
		pKey,
		rectDefs.map(([rKey, sizeMap]) => rect(rKey, sizeMap)),
		GAPS.messageParagraph,
	),
);

const messageFooterParagraphDefs: ReadonlyArray<[key: string, sizes: ReadonlyArray<[key: string, sizeMap: SizeMap]>]> =
	[
		[
			'messageFooterParagraph1',
			[
				[
					'messageFooterParagraph1Rect1',
					{ small: 'h-[3px] w-[88px]', medium: 'h-[7px] w-[100px]', large: 'h-[8px] w-[117px]' },
				],
			],
		],
		[
			'messageFooterParagraph2',
			[
				[
					'messageFooterParagraph2Rect1',
					{ small: 'h-[3px] w-[95px]', medium: 'h-[7px] w-[177px]', large: 'h-[8px] w-[211px]' },
				],
				[
					'messageFooterParagraph2Rect2',
					{ small: 'h-[3px] w-[80px]', medium: 'h-[7px] w-[162px]', large: 'h-[8px] w-[189px]' },
				],
			],
		],
		[
			'messageFooterParagraph3',
			[
				[
					'messageFooterParagraph3Rect1',
					{ small: 'h-[3px] w-[70px]', medium: 'h-[7px] w-[130px]', large: 'h-[8px] w-[152px]' },
				],
				[
					'messageFooterParagraph3Rect2',
					{ small: 'h-[3px] w-[92px]', medium: 'h-[7px] w-[171px]', large: 'h-[8px] w-[204px]' },
				],
			],
		],
		[
			'messageFooterParagraph4',
			[
				[
					'messageFooterParagraph4Rect1',
					{ small: 'h-[3px] w-[95px]', medium: 'h-[7px] w-[177px]', large: 'h-[8px] w-[211px]' },
				],
				[
					'messageFooterParagraph4Rect2',
					{ small: 'h-[3px] w-[80px]', medium: 'h-[7px] w-[162px]', large: 'h-[8px] w-[189px]' },
				],
				[
					'messageFooterParagraph4Rect3',
					{ small: 'h-[3px] w-[20px]', medium: 'h-[7px] w-[57px]', large: 'h-[8px] w-[66px]' },
				],
			],
		],
		[
			'messageFooterParagraph5',
			[
				[
					'messageFooterParagraph5Rect1',
					{ small: 'h-[3px] w-[95px]', medium: 'h-[7px] w-[177px]', large: 'h-[8px] w-[211px]' },
				],
			],
		],
		[
			'messageFooterParagraph6',
			[
				[
					'messageFooterParagraph6Rect1',
					{ small: 'h-[3px] w-[84px]', medium: 'h-[7px] w-[151px]', large: 'h-[8px] w-[176px]' },
				],
				[
					'messageFooterParagraph6Rect2',
					{ small: 'h-[3px] w-[95px]', medium: 'h-[7px] w-[177px]', large: 'h-[8px] w-[211px]' },
				],
				[
					'messageFooterParagraph6Rect3',
					{ small: 'h-[3px] w-[61px]', medium: 'h-[7px] w-[113px]', large: 'h-[8px] w-[132px]' },
				],
			],
		],
	];

const messageFooterParagraphs: readonly Paragraph[] = messageFooterParagraphDefs.map(([pKey, rectDefs]) =>
	paragraph(
		pKey,
		rectDefs.map(([rKey, sizeMap]) => rect(rKey, sizeMap)),
		GAPS.messageParagraph,
	),
);

export const shapes = {
	gaps: {
		small: 'gap-2',
		medium: 'gap-[15px]',
		large: 'gap-5',
	},
	petitionInfo: {
		petitionTitle: { gaps: GAPS.petitionTitle, rectangles: petitionTitleRectangles },
		petitionImage: rect('petitionImageRect1', {
			small: 'h-[21px] w-[27px] rounded-[2px]',
			medium: 'h-[42px] w-[55px] rounded-small',
			large: 'h-[45px] w-[59px] rounded-small',
		}),
	},
	message: { gaps: GAPS.message, paragraphs: messageParagraphs },
	messageFooter: { gaps: GAPS.messageFooter, paragraphs: messageFooterParagraphs },
} as const;
